import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EWMap, type EWMapApi } from "@/lib/map-engine";
import { WsClient } from "@/lib/ws-client";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";
import { SEVERITY_COLOR, RULE_AREA } from "@/types/api";
import type { Severity, Signal } from "@/types/api";

/**
 * useOps — owns the realtime data layer and the canvas render engine for the
 * Operations screen, and bridges them to React.
 *
 * Key design rule: the map reads the live object/signal arrays DIRECTLY and
 * renders at 60fps with zero React involvement. React state only updates at
 * human cadence (new/revised signals, connection changes) so the feed + detail
 * stay in sync without re-rendering on every position tick.
 */

export type ConnState = "connecting" | "live" | "reconnecting" | "resynced" | "simulated";
export type SortMode = "time" | "severity";

export interface Toast {
  id: string;
  signalId: string;
  severity: Severity;
  title: string;
  revised: boolean;
}

export interface OpsApi {
  ready: boolean;
  isLive: boolean;
  conn: ConnState;
  /** monotonically bumped whenever feed data changes — drives feed re-render */
  version: number;
  signals: Signal[];
  /** median internal latency (ms) from recent signals, or null if no data */
  medianLatencyMs: number | null;
  /** ingress→this-browser latency (ms) of the newest signal frame, clock-skew corrected */
  lastE2eMs: number | null;
  selectedSignalId: string | null;
  selectedEventId: string | null;
  sortMode: SortMode;
  sevFilter: Record<Severity, boolean>;
  toasts: Toast[];
  attachCanvas: (el: HTMLCanvasElement | null) => void;
  selectSignal: (id: string) => void;
  selectEvent: (id: string) => void;
  clearSelection: () => void;
  setSortMode: (m: SortMode) => void;
  toggleSeverity: (s: Severity) => void;
  toggleLayer: (k: string, on: boolean) => void;
  /** trading-area packs enabled — adds that area's lower-severity signal detail */
  enabledAreas: Set<string>;
  toggleArea: (a: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  getClient: () => WsClient | null;
  getMap: () => EWMapApi | null;
  /** bumped on every desk-chat change — drives the chat drawer + unread badge */
  chatTick: number;
}

const PHOSPHOR = "95, 211, 224"; // keep in sync with --green-rgb in tokens.css

const AREA_PREFS_KEY = "ew.areas";
function loadAreaPrefs(): Set<string> {
  try {
    const raw = localStorage.getItem(AREA_PREFS_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* ignore */
  }
  return new Set<string>(); // areas off by default → core (high/critical) feed only
}

export function useOps(): OpsApi {
  const clientRef = useRef<WsClient | null>(null);
  const mapRef = useRef<EWMapApi | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [ready, setReady] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [conn, setConn] = useState<ConnState>("connecting");
  const [version, setVersion] = useState(0);
  const [chatTick, setChatTick] = useState(0);
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>("time");
  const [sevFilter, setSevFilter] = useState<Record<Severity, boolean>>({
    critical: true,
    high: true,
    medium: true,
    low: true,
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [enabledAreas, setEnabledAreas] = useState<Set<string>>(loadAreaPrefs);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const sevFilterRef = useRef(sevFilter);
  sevFilterRef.current = sevFilter;
  const enabledAreasRef = useRef(enabledAreas);
  enabledAreasRef.current = enabledAreas;

  const pushToast = useCallback((s: Signal, revised: boolean) => {
    // Apply the same filter the feed uses: severity gate + trading-area gate
    if (!sevFilterRef.current[s.severity]) return;
    if (s.severity !== "high" && s.severity !== "critical") {
      const area = RULE_AREA[s.rule];
      if (area && !enabledAreasRef.current.has(area)) return;
    }

    const t: Toast = {
      id: `${s.id}:${s.updatedAt}`,
      signalId: s.id,
      severity: s.severity,
      title: s.title,
      revised,
    };
    setToasts((prev) => {
      const next = prev.filter((x) => x.signalId !== s.id).concat(t);
      return next.slice(-3);
    });
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), 6500);
  }, []);

  // ── lifecycle: create client + map once ──────────────────────────────
  useEffect(() => {
    let disposed = false;
    const client = new WsClient({
      wsUrl: config.wsUrl,
      getToken: getIdToken,
      callbacks: {
        onConn: (state) => {
          if (state === "offline" || state === "simulated") setConn("simulated");
          else setConn(state);
          if (state === "live") setIsLive(true);
        },
        onSnapshot: () => {
          mapRef.current?.markPositionsChanged();
          bump();
        },
        onSignal: (s) => {
          pushToast(s, false);
          bump();
        },
        onRevise: (s, info) => {
          if (info.sevChanged) pushToast(s, true);
          bump();
        },
        onEvent: () => bump(),
        onChat: () => setChatTick((t) => t + 1),
      },
    });
    clientRef.current = client;

    client
      .start()
      .then(() => {
        if (disposed) return;
        setIsLive(true);
        initMap(client);
      })
      .catch(() => {
        // Live unreachable. In production this should surface the
        // connection-lost screen; sim fallback is a dev convenience.
        if (disposed) return;
        setConn("simulated");
        initMap(client);
      });

    function initMap(c: WsClient) {
      if (!canvasRef.current) {
        // canvas not mounted yet — retry on next frame
        requestAnimationFrame(() => initMap(c));
        return;
      }
      const map = new EWMap(canvasRef.current, {
        live: true,
        objects: c.objects,
        events: c.events,
        signals: c.signals,
        colors: { phosphor: PHOSPHOR, severity: SEVERITY_COLOR },
      }) as unknown as EWMapApi;
      mapRef.current = map;
      setReady(true);
    }

    return () => {
      disposed = true;
      client.stop();
      clientRef.current = null;
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── canvas hover tooltip + click picking handled by the component ────
  const attachCanvas = useCallback((el: HTMLCanvasElement | null) => {
    canvasRef.current = el;
  }, []);

  const selectSignal = useCallback((id: string) => {
    const c = clientRef.current;
    const map = mapRef.current;
    const s = c?.signals.find((x) => x.id === id);
    if (!s || !map) return;
    setSelectedSignalId(id);
    setSelectedEventId(null);
    map.select(id);
    map.selectEvent(null);
    map.flyTo(s.lat, s.lon, 8);
  }, []);

  const selectEvent = useCallback((id: string) => {
    const c = clientRef.current;
    const map = mapRef.current;
    const e = c?.events.find((x) => x.id === id);
    if (!e || !map) return;
    setSelectedEventId(id);
    setSelectedSignalId(null);
    map.select(null);
    map.selectEvent(id);
    map.flyTo(e.lat, e.lon, Math.max(10, map.tk));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSignalId(null);
    setSelectedEventId(null);
    mapRef.current?.select(null);
    mapRef.current?.selectEvent(null);
    mapRef.current?.overview();
  }, []);

  const toggleSeverity = useCallback((s: Severity) => {
    setSevFilter((prev) => ({ ...prev, [s]: !prev[s] }));
  }, []);

  const toggleLayer = useCallback((k: string, on: boolean) => {
    const map = mapRef.current;
    if (!map) return;
    map.setLayer(k, on);
    if (k === "satellite") clientRef.current?.ensureSatellites(on);
  }, []);

  const toggleArea = useCallback((a: string) => {
    setEnabledAreas((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      try {
        localStorage.setItem(AREA_PREFS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const zoomIn = useCallback(() => {
    const m = mapRef.current;
    if (m) m.zoomAt(1.5, m.W / 2, m.H / 2);
  }, []);
  const zoomOut = useCallback(() => {
    const m = mapRef.current;
    if (m) m.zoomAt(1 / 1.5, m.W / 2, m.H / 2);
  }, []);

  const medianLatencyMs = useMemo(() => {
    const sigs = clientRef.current?.signals ?? [];
    const vals = sigs
      .map((s) => {
        if (s.engineIngressAt && s.enginePublishedAt)
          return s.enginePublishedAt - s.engineIngressAt;
        return null;
      })
      .filter((v): v is number => v != null && v >= 0)
      .sort((a, b) => a - b);
    if (vals.length === 0) return null;
    const mid = Math.floor(vals.length / 2);
    return vals.length % 2 === 0 ? (vals[mid - 1] + vals[mid]) / 2 : vals[mid];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  return {
    ready,
    isLive,
    conn,
    version,
    chatTick,
    signals: clientRef.current?.signals ?? [],
    medianLatencyMs,
    // Ingress→browser latency of the newest signal frame (clock-skew corrected in ws-client).
    lastE2eMs: clientRef.current?.lastE2eMs ?? null,
    selectedSignalId,
    selectedEventId,
    sortMode,
    sevFilter,
    toasts,
    attachCanvas,
    selectSignal,
    selectEvent,
    clearSelection,
    setSortMode,
    toggleSeverity,
    toggleLayer,
    enabledAreas,
    toggleArea,
    zoomIn,
    zoomOut,
    getClient: () => clientRef.current,
    getMap: () => mapRef.current,
  };
}

export { PHOSPHOR };
